import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Search, MapPin, Star, Shield, Zap, TrendingUp, ChevronLeft, Play, Users, Building2, Wrench } from 'lucide-react';
import api from '../utils/api';
import ListingCard from '../components/listings/ListingCard';

const SPORTS_CATEGORIES = [
  { slug: 'football-field', icon: '⚽', label: 'كرة القدم', count: '200+', type: 'venue' },
  { slug: 'padel-court', icon: '🎾', label: 'البادل', count: '80+', type: 'venue' },
  { slug: 'tennis-court', icon: '🏸', label: 'التنس', count: '60+', type: 'venue' },
  { slug: 'swimming-pool', icon: '🏊', label: 'المسابح', count: '40+', type: 'venue' },
  { slug: 'led-screens', icon: '📺', label: 'شاشات LED', count: '50+', type: 'equipment' },
  { slug: 'referees', icon: '👨‍⚖️', label: 'الحكام', count: '100+', type: 'service' },
  { slug: 'coaches', icon: '🏆', label: 'المدربون', count: '150+', type: 'service' },
  { slug: 'live-streaming', icon: '📡', label: 'البث المباشر', count: '30+', type: 'equipment' },
];

const CITIES = ['دبي', 'أبوظبي', 'الشارقة', 'عجمان', 'الرياض', 'جدة', 'القاهرة', 'عمّان'];

export default function HomePage() {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [city, setCity] = useState('');
  const [type, setType] = useState('');
  const [featuredListings, setFeaturedListings] = useState([]);
  const [stats, setStats] = useState({ listings: 500, users: 2000, bookings: 8000 });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchFeatured();
  }, []);

  const fetchFeatured = async () => {
    try {
      const res = await api.get('/listings?featured=true&limit=6&status=active');
      setFeaturedListings(res.data.data || []);
    } catch {
      setFeaturedListings([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    const params = new URLSearchParams();
    if (search) params.set('q', search);
    if (city) params.set('city', city);
    if (type) params.set('type', type);
    navigate(`/search?${params.toString()}`);
  };

  return (
    <div className="min-h-screen">
      {/* ============ HERO ============ */}
      <section className="relative min-h-screen flex items-center overflow-hidden">
        {/* Background */}
        <div className="absolute inset-0 z-0">
          <div className="absolute inset-0 bg-gradient-to-br from-dark-900 via-dark-900/95 to-dark-800" />
          <div className="absolute top-0 right-0 w-1/2 h-full opacity-10"
            style={{ background: 'radial-gradient(ellipse at top right, #22c55e 0%, transparent 60%)' }} />
          <div className="absolute bottom-0 left-0 w-1/3 h-2/3 opacity-5"
            style={{ background: 'radial-gradient(ellipse at bottom left, #f59e0b 0%, transparent 60%)' }} />
          {/* Grid pattern */}
          <div className="absolute inset-0 opacity-5"
            style={{ backgroundImage: 'linear-gradient(#22c55e 1px, transparent 1px), linear-gradient(90deg, #22c55e 1px, transparent 1px)', backgroundSize: '50px 50px' }} />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 pt-24 pb-16 w-full">
          <div className="max-w-3xl">
            {/* Badge */}
            <div className="inline-flex items-center gap-2 bg-primary-500/10 border border-primary-500/30 rounded-full px-4 py-1.5 mb-6 animate-fade-in">
              <span className="w-2 h-2 bg-primary-400 rounded-full animate-pulse" />
              <span className="text-primary-400 text-sm font-medium">أول منصة تأجير رياضية في المنطقة</span>
            </div>

            <h1 className="text-5xl md:text-7xl font-bold text-white leading-tight mb-6 animate-slide-up">
              احجز ملعبك
              <span className="block gradient-text">الآن في دقائق</span>
            </h1>

            <p className="text-dark-300 text-lg md:text-xl mb-10 leading-relaxed max-w-2xl">
              أكبر منصة لتأجير الملاعب والمعدات والخدمات الرياضية. آلاف الخيارات في متناول يدك.
            </p>

            {/* Search Bar */}
            <form onSubmit={handleSearch} className="bg-dark-800/90 border border-dark-600 rounded-2xl p-3 shadow-2xl backdrop-blur-sm flex flex-col md:flex-row gap-3 animate-slide-up">
              <div className="flex-1 flex items-center gap-3 bg-dark-700 rounded-xl px-4 py-2.5">
                <Search size={18} className="text-dark-400" />
                <input type="text" value={search} onChange={e => setSearch(e.target.value)}
                  placeholder="ابحث عن ملعب، معدة، أو خدمة..."
                  className="bg-transparent text-white placeholder-dark-400 flex-1 text-sm outline-none" />
              </div>

              <div className="flex items-center gap-3 bg-dark-700 rounded-xl px-4 py-2.5 min-w-[140px]">
                <MapPin size={16} className="text-dark-400" />
                <select value={city} onChange={e => setCity(e.target.value)}
                  className="bg-transparent text-white text-sm outline-none flex-1 cursor-pointer">
                  <option value="">جميع المدن</option>
                  {CITIES.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>

              <div className="flex items-center gap-3 bg-dark-700 rounded-xl px-4 py-2.5 min-w-[130px]">
                <select value={type} onChange={e => setType(e.target.value)}
                  className="bg-transparent text-white text-sm outline-none flex-1 cursor-pointer">
                  <option value="">كل الأنواع</option>
                  <option value="venue">ملاعب</option>
                  <option value="equipment">معدات</option>
                  <option value="service">خدمات</option>
                </select>
              </div>

              <button type="submit" className="bg-primary-600 hover:bg-primary-500 text-white font-bold px-8 py-2.5 rounded-xl transition-all flex items-center gap-2">
                <Search size={16} />
                بحث
              </button>
            </form>

            {/* Quick filters */}
            <div className="flex items-center gap-2 mt-4 flex-wrap">
              <span className="text-dark-500 text-xs">الأكثر بحثاً:</span>
              {['ملاعب كرة القدم', 'البادل دبي', 'مسابح للتأجير', 'حكام كرة قدم'].map(t => (
                <button key={t} onClick={() => { setSearch(t); }}
                  className="text-dark-400 hover:text-primary-400 text-xs bg-dark-700 hover:bg-dark-600 px-3 py-1 rounded-full transition-colors">
                  {t}
                </button>
              ))}
            </div>
          </div>

          {/* Stats floating */}
          <div className="absolute left-4 md:left-16 top-1/3 hidden lg:flex flex-col gap-4">
            {[
              { value: '500+', label: 'إعلان نشط', icon: Building2 },
              { value: '2K+', label: 'مستخدم', icon: Users },
              { value: '8K+', label: 'حجز مكتمل', icon: Star },
            ].map(({ value, label, icon: Icon }) => (
              <div key={label} className="glass rounded-2xl p-3 text-center min-w-[100px]">
                <Icon size={18} className="text-primary-400 mx-auto mb-1" />
                <div className="text-white font-bold text-lg">{value}</div>
                <div className="text-dark-400 text-xs">{label}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Scroll indicator */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 animate-bounce">
          <div className="w-5 h-8 border-2 border-dark-500 rounded-full flex items-start justify-center p-1">
            <div className="w-1 h-2 bg-primary-400 rounded-full" />
          </div>
        </div>
      </section>

      {/* ============ CATEGORIES ============ */}
      <section className="py-16 max-w-7xl mx-auto px-4">
        <div className="text-center mb-10">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-3">استكشف حسب الفئة</h2>
          <p className="text-dark-400">أكثر من 20 فئة رياضية متاحة</p>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-3">
          {SPORTS_CATEGORIES.map(({ slug, icon, label, count, type }) => (
            <Link key={slug} to={`/listings?category=${slug}&type=${type}`}
              className="group bg-dark-800 hover:bg-dark-700 border border-dark-700 hover:border-primary-500/50 rounded-2xl p-4 text-center transition-all duration-200 hover:-translate-y-1">
              <div className="text-3xl mb-2">{icon}</div>
              <div className="text-white text-xs font-semibold mb-1">{label}</div>
              <div className="text-primary-400 text-xs">{count}</div>
            </Link>
          ))}
        </div>
      </section>

      {/* ============ HOW IT WORKS ============ */}
      <section className="py-16 bg-dark-800/50">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-3">كيف يعمل SPORTIX؟</h2>
            <p className="text-dark-400">بسيط، سريع، وآمن</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { step: '01', icon: Search, title: 'ابحث', desc: 'ابحث عن الملعب أو المعدة أو الخدمة التي تحتاجها بسهولة' },
              { step: '02', icon: Building2, title: 'اختر', desc: 'قارن الخيارات واقرأ التقييمات واختر الأنسب لك' },
              { step: '03', icon: Zap, title: 'احجز', desc: 'احجز في ثوانٍ وادفع بأمان عبر بوابات الدفع الإلكتروني' },
              { step: '04', icon: Star, title: 'استمتع', desc: 'أظهر رمز QR عند الوصول وقيّم تجربتك بعد الانتهاء' },
            ].map(({ step, icon: Icon, title, desc }) => (
              <div key={step} className="relative">
                <div className="bg-dark-800 border border-dark-700 rounded-2xl p-6 h-full">
                  <div className="text-primary-400/30 font-bold text-5xl mb-4 font-display">{step}</div>
                  <div className="w-10 h-10 bg-primary-600/20 rounded-xl flex items-center justify-center mb-4">
                    <Icon size={20} className="text-primary-400" />
                  </div>
                  <h3 className="text-white font-bold text-lg mb-2">{title}</h3>
                  <p className="text-dark-400 text-sm leading-relaxed">{desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ============ FEATURED LISTINGS ============ */}
      <section className="py-16 max-w-7xl mx-auto px-4">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h2 className="text-3xl font-bold text-white mb-1">إعلانات مميزة</h2>
            <p className="text-dark-400 text-sm">أفضل الملاعب والمعدات المتاحة الآن</p>
          </div>
          <Link to="/listings" className="flex items-center gap-1 text-primary-400 hover:text-primary-300 text-sm transition-colors">
            عرض الكل <ChevronLeft size={14} />
          </Link>
        </div>

        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[...Array(6)].map((_, i) => (
              <div key={i} className="bg-dark-800 rounded-2xl overflow-hidden">
                <div className="skeleton h-48" />
                <div className="p-4 space-y-3">
                  <div className="skeleton h-4 rounded-lg" />
                  <div className="skeleton h-3 rounded-lg w-2/3" />
                </div>
              </div>
            ))}
          </div>
        ) : featuredListings.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {featuredListings.map(listing => <ListingCard key={listing.id} listing={listing} />)}
          </div>
        ) : (
          <div className="text-center py-16 text-dark-400">
            <Building2 size={40} className="mx-auto mb-3 opacity-30" />
            <p>سيتم عرض الإعلانات المميزة هنا</p>
            <Link to="/register" className="btn-primary mt-4 inline-flex">أضف إعلانك الأول</Link>
          </div>
        )}
      </section>

      {/* ============ FOR OWNERS CTA ============ */}
      <section className="py-16 bg-gradient-to-r from-primary-900/40 via-dark-800 to-dark-900 border-y border-primary-500/20">
        <div className="max-w-7xl mx-auto px-4 flex flex-col md:flex-row items-center justify-between gap-8">
          <div>
            <h2 className="text-3xl font-bold text-white mb-3">هل تمتلك ملعباً أو معدات؟</h2>
            <p className="text-dark-300 text-lg mb-2">حقق دخلاً إضافياً من أصولك الرياضية</p>
            <div className="flex flex-wrap gap-4 mt-4">
              {[
                { icon: TrendingUp, text: 'دخل ثابت شهري' },
                { icon: Shield, text: 'مدفوعات آمنة' },
                { icon: Zap, text: 'إدارة سهلة' },
              ].map(({ icon: Icon, text }) => (
                <div key={text} className="flex items-center gap-2 text-dark-300 text-sm">
                  <Icon size={16} className="text-primary-400" />
                  {text}
                </div>
              ))}
            </div>
          </div>
          <Link to="/register" className="flex-shrink-0 bg-primary-600 hover:bg-primary-500 text-white font-bold px-10 py-4 rounded-2xl transition-all text-lg shadow-lg shadow-primary-900/50">
            ابدأ الكسب الآن ←
          </Link>
        </div>
      </section>

      {/* ============ FEATURES ============ */}
      <section className="py-16 max-w-7xl mx-auto px-4">
        <div className="text-center mb-12">
          <h2 className="text-3xl font-bold text-white mb-3">لماذا SPORTIX؟</h2>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
          {[
            { icon: '🔒', title: 'دفع آمن', desc: '100% مضمون' },
            { icon: '⚡', title: 'حجز فوري', desc: 'في ثوانٍ' },
            { icon: '📱', title: 'QR Access', desc: 'دخول ذكي' },
            { icon: '💬', title: 'تواصل مباشر', desc: 'مع المالك' },
            { icon: '⭐', title: 'تقييمات', desc: 'موثوقة' },
            { icon: '📊', title: 'تقارير', desc: 'تفصيلية' },
          ].map(({ icon, title, desc }) => (
            <div key={title} className="bg-dark-800 border border-dark-700 rounded-2xl p-4 text-center hover:border-primary-500/50 transition-colors">
              <div className="text-3xl mb-2">{icon}</div>
              <div className="text-white text-sm font-semibold">{title}</div>
              <div className="text-dark-400 text-xs mt-1">{desc}</div>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}
